import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Linkedin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';

/* ── Logos ── */
const LOGO_OE = '/images/logos/oe-logo.png';
const LOGO_IOM3 = '/images/logos/iom3-logo.jpg';
const LOGO_FIB = '/images/logos/fib-logo.png';

/* ── Data ── */
const INDEPENDENT_ADVISERS = [
  {
    id: 'nuno',
    name: 'Nuno Mesquita Ferreira',
    credKey: 'people.nuno_credentials',
    titleKey: 'people.nuno_title',
    photo: '/images/nuno-ferreira.jpg',
    linkedin: 'https://www.linkedin.com/in/nffer/',
    bioKey: 'people.nuno_bio',
    eduKeys: ['people.nuno_edu_1', 'people.nuno_edu_2'],
    experience: [
      { c: 'people.nuno_exp_1_company', r: 'people.nuno_exp_1_role', y: 'people.nuno_exp_1_years' },
      { c: 'people.nuno_exp_2_company', y: 'people.nuno_exp_2_years', subRoles: [
        { r: 'people.nuno_exp_2a_role', y: 'people.nuno_exp_2a_years' },
        { r: 'people.nuno_exp_2b_role', y: 'people.nuno_exp_2b_years' },
        { r: 'people.nuno_exp_2c_role', y: 'people.nuno_exp_2c_years' },
      ]},
      { c: 'people.nuno_exp_4_company', r: 'people.nuno_exp_4_role', y: 'people.nuno_exp_4_years' },
      { c: 'people.nuno_exp_5_company', r: 'people.nuno_exp_5_role', y: 'people.nuno_exp_5_years' },
      { c: 'people.nuno_exp_6_company', r: 'people.nuno_exp_6_role', y: 'people.nuno_exp_6_years' },
      { c: 'people.nuno_exp_7_company', r: 'people.nuno_exp_7_role', y: 'people.nuno_exp_7_years' },
    ],
    memberships: [
      { orgKey: 'people.nuno_mem_oe_org', roleKey: 'people.nuno_mem_oe_role', url: 'https://www.ordemengenheiros.pt', logo: LOGO_OE },
      { orgKey: 'people.nuno_mem_iom3_org', roleKey: 'people.nuno_mem_iom3_role', url: 'https://www.iom3.org', logo: LOGO_IOM3 },
      { orgKey: 'people.nuno_mem_fib_org', roleKey: 'people.nuno_mem_fib_role', url: 'https://www.fib-international.org/commissions/com8-durability.html', logo: LOGO_FIB },
    ],
    pubKeys: ['people.nuno_pub_1', 'people.nuno_pub_2'],
  },
  {
    id: 'joao',
    name: 'João Pedro Sobral Amaro',
    credKey: 'people.joao_credentials',
    titleKey: 'people.joao_title',
    photo: '/joao-amaro.png',
    linkedin: 'https://www.linkedin.com/in/jo%C3%A3o-amaro-3a2097229/',
    bioKey: 'people.joao_bio',
    eduKeys: ['people.joao_edu_1'],
    experience: [
      { c: 'people.joao_exp_1_company', r: 'people.joao_exp_1_role', y: 'people.joao_exp_1_years' },
      { c: 'people.joao_exp_2_company', r: 'people.joao_exp_2_role', y: 'people.joao_exp_2_years' },
    ],
    memberships: [
      { orgKey: 'people.joao_mem_oe_org', roleKey: 'people.joao_mem_oe_role', url: 'https://www.ordemengenheiros.pt', logo: LOGO_OE },
    ],
    pubKeys: [],
  },
];

const ALL_PEOPLE = [...INDEPENDENT_ADVISERS];

/* ── Section heading ── */
const SH = ({ children }) => (
  <h2 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>{children}</h2>
);

/* ═══════════════ GRID CARD ═══════════════ */
const PersonCard = ({ person, t }) => (
  <a
    href={`#profile-${person.id}`}
    className="group relative block overflow-hidden cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
    style={{ width: 280, height: 320, borderRadius: 'var(--radius-md, 6px)' }}
    data-testid={`person-card-${person.id}`}
  >
    <img
      src={person.photo}
      alt={person.name}
      className="w-full h-full object-cover object-top transition-[filter] duration-[320ms] ease-in-out grayscale group-hover:grayscale-0 motion-reduce:grayscale-0"
    />
    {/* Hover overlay */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 motion-reduce:opacity-100 transition-opacity duration-[320ms]" style={{ background: 'linear-gradient(to top, rgba(10,10,10,0.85) 0%, transparent 55%)' }}>
      <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 motion-reduce:translate-y-0 opacity-0 group-hover:opacity-100 motion-reduce:opacity-100 transition-all duration-[250ms]" style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}>
        <p className="text-base font-semibold text-white leading-tight">{person.name}</p>
        <p className="text-xs text-gray-400 italic mt-0.5">{t(person.credKey)}</p>
        <p className="text-sm text-green-500 uppercase tracking-wider mt-1" style={{ fontSize: 11 }}>{t(person.titleKey)}</p>
        <p className="text-xs text-green-500 mt-3 flex items-center gap-1">
          {t('people.view_profile')} <ArrowRight className="w-3 h-3" />
        </p>
      </div>
    </div>
  </a>
);

/* ═══════════════ INDIVIDUAL PROFILE ═══════════════ */
const PersonProfile = ({ person, t }) => (
  <div className="max-w-5xl mx-auto" data-testid={`profile-${person.id}`}>
    {/* Back link */}
    <a href="#people" className="inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-600 transition-colors mb-8" data-testid="back-to-people">
      <ArrowLeft className="w-4 h-4" /> {t('people.back_to_people')}
    </a>

    <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
      {/* Left column */}
      <div className="flex-shrink-0">
        <div className="overflow-hidden mx-auto lg:mx-0" style={{ width: 280, height: 320, borderRadius: 'var(--radius-md, 6px)' }}>
          <img src={person.photo} alt={person.name} className="w-full h-full object-cover object-top" />
        </div>

        {/* Memberships — left on desktop, bottom on mobile */}
        {person.memberships.length > 0 && (
          <div className="mt-8 hidden lg:block" style={{ width: 280 }}>
            <h2 className="text-xs font-semibold tracking-[0.12em] uppercase text-gray-400 mb-4" style={{ fontFamily: 'Outfit,sans-serif' }}>
              {t('people.memberships_label')}
            </h2>
            <div className="grid gap-y-3 gap-x-4" style={{ gridTemplateColumns: '56px 1fr', alignItems: 'center' }}>
              {person.memberships.map((m, i) => (
                <MembershipRow key={i} m={m} t={t} i={i} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right column */}
      <div className="flex-1 min-w-0 max-w-[700px]">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight" style={{ fontFamily: 'Outfit,sans-serif' }} data-testid="profile-name">
          {person.name}
        </h1>
        <p className="text-sm text-gray-500 italic mt-1">{t(person.credKey)}</p>
        <p className="text-sm text-green-700 uppercase tracking-wider mt-1 font-medium" style={{ letterSpacing: '0.08em' }}>{t(person.titleKey)}</p>

        {person.linkedin && (
          <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-600 transition-colors mt-3" data-testid={`linkedin-${person.id}`}>
            <Linkedin className="w-4 h-4" /> {t('people.linkedin_profile')}
          </a>
        )}

        <hr className="border-gray-200 my-6" />

        {/* About */}
        <div className="mb-8">
          <SH>{t('people.about_label')}</SH>
          {t(person.bioKey).split('\n\n').map((para, i) => (
            <p key={i} className="text-sm text-gray-600 leading-relaxed mb-3">{para}</p>
          ))}
        </div>

        {/* Education */}
        <div className="mb-8">
          <SH>{t('people.education_label')}</SH>
          <ul className="space-y-1.5">
            {person.eduKeys.map((key, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-1 h-1 bg-green-600 rounded-full mt-2 flex-shrink-0" />
                <span className="text-sm text-gray-600 leading-relaxed">{t(key)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Experience */}
        <div className="mb-8">
          <SH>{t('people.experience_label')}</SH>
          <div className="space-y-0">
            {person.experience.map((exp, i) => (
              <div key={i} className={`py-2.5 ${i < person.experience.length - 1 ? 'border-b border-gray-100' : ''}`}>
                {exp.subRoles ? (
                  /* Grouped entry (e.g. multiple roles at one company) */
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <span className="text-sm font-semibold text-gray-900">{t(exp.c)}</span>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">{t(exp.y)}</span>
                    </div>
                    <div className="mt-1.5 ml-3 border-l-2 border-gray-100 pl-3 space-y-1">
                      {exp.subRoles.map((sr, j) => (
                        <div key={j} className="flex items-start justify-between gap-4">
                          <span className="text-sm text-gray-500">{t(sr.r)}</span>
                          <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{t(sr.y)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Standard single-role entry */
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-gray-900">{t(exp.c)}</span>
                      <span className="text-sm text-gray-500 ml-2">{t(exp.r)}</span>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-0.5">{t(exp.y)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Memberships — mobile only (below bio) */}
        {person.memberships.length > 0 && (
          <div className="mb-8 lg:hidden">
            <SH>{t('people.memberships_label')}</SH>
            <div className="grid gap-y-3 gap-x-4" style={{ gridTemplateColumns: '56px 1fr', alignItems: 'center' }}>
              {person.memberships.map((m, i) => (
                <MembershipRow key={i} m={m} t={t} i={i} />
              ))}
            </div>
          </div>
        )}

        {/* Publications */}
        {person.pubKeys.length > 0 && (
          <div className="mb-8">
            <SH>{t('people.publications_label')}</SH>
            <ul className="space-y-3">
              {person.pubKeys.map((key, i) => (
                <li key={i} className="text-sm text-gray-600 leading-relaxed pl-4 border-l-2 border-gray-200">
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
);

/* ── Membership Row (reusable) ── */
const MembershipRow = ({ m, t, i }) => (
  <>
    <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`membership-logo-${i}`}>
      <img
        src={m.logo}
        alt=""
        className="w-full object-contain object-center transition-all duration-200 grayscale opacity-65 hover:grayscale-0 hover:opacity-100"
        style={{ height: 36 }}
        loading="lazy"
      />
    </a>
    <div className="leading-snug">
      <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-800 hover:text-green-700 transition-colors">
        {t(m.orgKey)}
      </a>
      <span className="block text-xs text-gray-400 mt-0.5">{t(m.roleKey)}</span>
    </div>
  </>
);

/* ═══════════════ MAIN PAGE ═══════════════ */
export const People = () => {
  const { t } = useTranslation();
  const [activeProfile, setActiveProfile] = useState(null);

  useEffect(() => {
    const parseHash = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#profile-')) {
        const id = hash.replace('#profile-', '');
        const person = ALL_PEOPLE.find(p => p.id === id);
        setActiveProfile(person || null);
      } else {
        setActiveProfile(null);
      }
    };
    parseHash();
    window.addEventListener('hashchange', parseHash);
    return () => window.removeEventListener('hashchange', parseHash);
  }, []);

  // Scroll to top when switching views
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeProfile]);

  return (
    <div className="min-h-screen pt-16" style={{ fontFamily: 'IBM Plex Sans,sans-serif' }}>
      <SEO title={`${activeProfile ? activeProfile.name : t('people.title')} | Durable Designs`} description={t('people.subtitle')} path="/people" />

      {!activeProfile ? (
        /* ════ GRID VIEW ════ */
        <>
          <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ background: '#06120B' }}>
            <div className="max-w-7xl mx-auto relative z-10">
              <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tighter mb-4" style={{ fontFamily: 'Plus Jakarta Sans,sans-serif' }} data-testid="people-heading">
                {t('people.title')}
              </h1>
              <p className="text-base text-neutral-400 max-w-2xl mb-2">{t('people.subtitle')}</p>
              <Link to="/engineering/people/careers" className="text-sm text-emerald-400 hover:text-white transition-colors" data-testid="view-careers-link">
                {t('people.view_careers')}
              </Link>
            </div>
          </section>

          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 justify-items-center" data-testid="people-grid">
                {ALL_PEOPLE.map((p, idx) => (
                  <div key={p.id} className="flex flex-col items-center">
                    {idx === 0 && (
                      <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-gray-400 mb-3 self-start" style={{ fontFamily: 'Outfit,sans-serif' }}>
                        {t('people.advisers_label')}
                      </span>
                    )}
                    {idx !== 0 && (
                      <span className="mb-3 self-start" style={{ height: '14px' }} />
                    )}
                    <PersonCard person={p} t={t} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : (
        /* ════ PROFILE VIEW ════ */
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white min-h-[80vh]">
          <PersonProfile person={activeProfile} t={t} />
        </section>
      )}
    </div>
  );
};

export default People;
